// Implementação real de @react-native-firebase/remote-config para o ambiente web,
// via REST API do Firebase (Installations + Remote Config), já que o SDK nativo
// não roda no navegador.

import RNConfig from './config'

const PROJECT_NUMBER = RNConfig.RN_FIREBASE_PROJECT_NUMBER
const API_KEY = RNConfig.RN_FIREBASE_API_KEY
const APP_ID = RNConfig.RN_FIREBASE_APP_ID

const INSTALLATIONS_STORAGE_KEY = '@baratao:firebase_installation'
const SDK_VERSION = 'w:1.0.0'

type RemoteConfigValue = {
  asString: () => string
  asNumber: () => number
  asBoolean: () => boolean
}

type Installation = {
  fid: string
  authToken: string
  expiresAt: number
}

let defaults: Record<string, any> = {}
let activeValues: Record<string, string> = {}
let installationPromise: Promise<Installation | null> | null = null
let fetchPromise: Promise<void> | null = null

function generateFid(): string {
  const bytes = new Uint8Array(17)

  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256)
    }
  }

  // Versão 0111 exigida pelo formato de Installation ID do Firebase
  bytes[0] = 0b01110000 + (bytes[0] % 0b00010000)

  let binary = ''
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte)
  })

  const base64 =
    typeof window !== 'undefined' && window.btoa
      ? window.btoa(binary)
      : Buffer.from(binary, 'binary').toString('base64')

  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '').substring(0, 22)
}

function loadStoredInstallation(): Installation | null {
  try {
    const raw = window.localStorage.getItem(INSTALLATIONS_STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw)
    if (!parsed?.fid || !parsed?.authToken || !parsed?.expiresAt) return null

    return parsed
  } catch {
    return null
  }
}

function storeInstallation(installation: Installation) {
  try {
    window.localStorage.setItem(INSTALLATIONS_STORAGE_KEY, JSON.stringify(installation))
  } catch {
    // localStorage indisponível (modo privado, etc.) - segue sem persistir
  }
}

async function registerInstallation(): Promise<Installation | null> {
  const fid = generateFid()

  try {
    const response = await fetch(
      `https://firebaseinstallations.googleapis.com/v1/projects/${PROJECT_NUMBER}/installations`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-goog-api-key': API_KEY,
        },
        body: JSON.stringify({
          fid,
          authVersion: 'FIS_v2',
          appId: APP_ID,
          sdkVersion: SDK_VERSION,
        }),
      },
    )

    if (!response.ok) {
      throw new Error(`Installations request failed: ${response.status}`)
    }

    const data = await response.json()
    const expiresInSeconds = parseInt(data?.authToken?.expiresIn, 10) || 0

    const installation: Installation = {
      fid: data.fid || fid,
      authToken: data.authToken?.token,
      expiresAt: Date.now() + expiresInSeconds * 1000,
    }

    if (!installation.authToken) {
      throw new Error('Installations response missing authToken')
    }

    storeInstallation(installation)
    return installation
  } catch (error) {
    console.warn('[RemoteConfig] Falha ao registrar installation:', error)
    return null
  }
}

function ensureInstallation(): Promise<Installation | null> {
  if (installationPromise) {
    return installationPromise
  }

  installationPromise = (async () => {
    const stored = loadStoredInstallation()

    if (stored && stored.expiresAt > Date.now() + 60_000) {
      return stored
    }

    return registerInstallation()
  })()

  installationPromise.finally(() => {
    installationPromise = null
  })

  return installationPromise
}

function toValue(key: string): RemoteConfigValue {
  const raw = activeValues[key]
  const fallback = defaults[key]
  const value = raw !== undefined ? raw : fallback !== undefined ? String(fallback) : ''

  return {
    asString: () => value,
    asNumber: () => {
      const parsed = Number(value)
      return Number.isNaN(parsed) ? 0 : parsed
    },
    asBoolean: () => value === 'true' || value === '1',
  }
}

async function performFetch(): Promise<void> {
  if (!PROJECT_NUMBER || !API_KEY || !APP_ID) {
    console.warn('[RemoteConfig] Configuração do Firebase ausente, usando apenas defaults.')
    return
  }

  if (fetchPromise) {
    return fetchPromise
  }

  fetchPromise = (async () => {
    try {
      const installation = await ensureInstallation()
      if (!installation) return

      const response = await fetch(
        `https://firebaseremoteconfig.googleapis.com/v1/projects/${PROJECT_NUMBER}/namespaces/firebase:fetch?key=${API_KEY}`,
        {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            appId: APP_ID,
            appInstanceId: installation.fid,
            appInstanceIdToken: installation.authToken,
            sdkVersion: SDK_VERSION,
            languageCode:
              typeof navigator !== 'undefined' ? navigator.language : 'pt-BR',
          }),
        },
      )

      if (!response.ok) {
        throw new Error(`Remote Config fetch failed: ${response.status}`)
      }

      const data = await response.json()
      activeValues = data?.entries || {}
    } catch (error) {
      console.warn('[RemoteConfig] Falha ao buscar valores remotos:', error)
    }
  })()

  try {
    await fetchPromise
  } finally {
    fetchPromise = null
  }
}

const remoteConfig = () => ({
  setConfigSettings: (_settings: any) => Promise.resolve(),

  setDefaults: (newDefaults: Record<string, any>) => {
    defaults = {...defaults, ...newDefaults}
    return Promise.resolve()
  },

  fetch: (_expiration?: number) => performFetch(),

  fetchAndActivate: async () => {
    await performFetch()
    return true
  },

  activate: async () => true,

  getValue: (key: string) => toValue(key),

  getAll: () => {
    const merged: Record<string, RemoteConfigValue> = {}
    Object.keys({...defaults, ...activeValues}).forEach(key => {
      merged[key] = toValue(key)
    })
    return merged
  },
})

export default remoteConfig
